/**
 * AbsenceErrorHandler - Domain-specific error handling for absence operations
 */

import { HttpError } from '../../../core/services/HttpService';
import dialog from '../../../shared/utils/dialog';

interface ErrorHandler {
  handle(error: HttpError): boolean;
}

class AbsenceErrorHandler implements ErrorHandler {
  /**
   * Handle generic absence-related errors
   */
  handle(error: HttpError): boolean {
    // Try specific handlers first
    if (this.handleFetchError(error)) return true;
    if (this.handleCreateError(error)) return true;
    if (this.handleUpdateError(error)) return true;
    if (this.handleDeleteError(error)) return true;
    if (this.handleApprovalError(error)) return true;
    if (this.handleValidationError(error)) return true;

    // Not handled by absence-specific logic
    return false;
  }

  /**
   * Handle errors when fetching absences
   */
  handleFetchError(error: HttpError): boolean {
    if (error.status === 403) {
      dialog.showToast('You do not have permission to view absences');
      return true;
    }
    if (error.status === 404) {
      dialog.showToast('Absence data not found');
      return true;
    }
    return false;
  }

  /**
   * Handle errors when creating an absence
   */
  handleCreateError(error: HttpError): boolean {
    if (error.status === 400) {
      const errorData = error.data;
      if (errorData?.start_datetime || errorData?.end_datetime) {
        dialog.showToast('Invalid absence dates. Please check your start and end dates.');
        return true;
      }
      if (errorData?.employee) {
        dialog.showToast('Please select a valid employee');
        return true;
      }
      if (errorData?.absence_type) {
        dialog.showToast('Please select a valid absence type');
        return true;
      }
      dialog.showToast('Invalid absence data. Please check all fields.');
      return true;
    }
    if (error.status === 409) {
      dialog.showToast('This absence conflicts with an existing absence or shift');
      return true;
    }
    return false;
  }

  /**
   * Handle errors when updating an absence
   */
  handleUpdateError(error: HttpError): boolean {
    if (error.status === 400) {
      dialog.showToast('Invalid absence data. Please check all fields.');
      return true;
    }
    if (error.status === 403) {
      dialog.showToast('You do not have permission to update this absence');
      return true;
    }
    if (error.status === 404) {
      dialog.showToast('Absence not found');
      return true;
    }
    if (error.status === 409) {
      dialog.showToast('This absence update conflicts with existing data');
      return true;
    }
    return false;
  }

  /**
   * Handle errors when deleting an absence
   */
  handleDeleteError(error: HttpError): boolean {
    if (error.status === 403) {
    dialog.showToast('You do not have permission to delete this absence');
      return true;
    }
    if (error.status === 404) {
      dialog.showToast('Absence not found');
      return true;
    }
    if (error.status === 409) {
      dialog.showToast('Cannot delete this absence due to existing dependencies');
      return true;
    }
    return false;
  }

  /**
   * Handle errors when approving/rejecting absences
   */
  handleApprovalError(error: HttpError): boolean {
    if (error.status === 400) {
      dialog.showToast('Invalid absence status change request');
      return true;
    }
    if (error.status === 403) {
      dialog.showToast('You do not have permission to change absence status');
      return true;
    }
    if (error.status === 404) {
      dialog.showToast('Absence not found');
      return true;
    }
    if (error.status === 409) {
      dialog.showToast('Cannot change status of this absence');
      return true;
    }
    return false;
  }

  /**
   * Handle validation errors
   */
  handleValidationError(error: HttpError): boolean {
    if (error.status === 422) {
      const errorData = error.data;
      if (errorData?.detail) {
        dialog.showToast(`Validation error: ${errorData.detail}`);
      } else {
        dialog.showToast('Validation error: Please check your input data');
      }
      return true;
    }
    return false;
  }
}

// Export singleton instance
const absenceErrorHandler = new AbsenceErrorHandler();
export default absenceErrorHandler; 